/**
 * Horizontal blocking inside a branch. The wall reads by its branch level (brand): bands of
 * its own and no columns, rows; columns and no bands, columns; neither, by whether the
 * branches stack their levels in rows. On a horizontal wall the levels inside a brand are
 * rows: sprays on their own shelf under the sticks, a line's row under another's. Those
 * levels are read coarsest row first and ship at cdt2; everything else sorts inside them. A
 * vertical wall never reaches this reading, so its trees are untouched, unless the merchant
 * sets the wall to rows in the studio (`chooseWallBlocking`); a close call (`isCloseCall`)
 * is the studio's cue to check.
 *
 * Input must be the merchant's base pog, never a layout this pipeline produced.
 */
import {
  type BlockingScope,
  VERTICAL_SCORE,
  measureBandednessOf,
  measureBlockingOf,
  rootBlockingScopes,
  splitBlockingScopes,
} from "./blockOrientation";
import { isUntouchedSinceInference, type StudioHistory } from "./studioHistory";
import {
  isPlaceholderValue,
  type StudioDraft,
  type StudioItem,
  type StudioRunGeometry,
  type WallBlocking,
  type WallBlockingKind,
} from "./studioTypes";

/** At or above this bandedness a level's values own disjoint rows. */
export const ROW_BAND_SCORE = 0.5;
/** Two row levels this close in bandedness tie; the coarser one (fewer values) nests outside. */
export const ROW_TIE_MARGIN = 0.1;
/** Share of the wall's branches (by SKUs) that must read horizontal for the wall to read horizontal. */
export const HORIZONTAL_BRANCH_SHARE = 0.5;
/** A block holds at least this many SKUs; one SKU apart is a sort, not a row. */
export const MIN_ROW_BLOCK_ITEMS = 2;
/** Share of a scope's carrying SKUs its real blocks must hold. */
export const ROW_BLOCK_COVERAGE = 0.9;
/** Row levels compounded into cdt2 at most: form + line. Past that cdt2 names products. */
export const MAX_ROW_LEVELS = 2;

const valueOf = (item: StudioItem, attr: string): string => String(item.attrs[attr] ?? "");

/** `attr` cuts these items into real blocks: two or more values of two or more SKUs that
 *  together hold nearly every SKU carrying a value. Dove's packaging (43 unboxed, 1 boxed)
 *  and Native's pack type (30 singles, 1 twin) are one block and a stray, not two rows. */
const blocksItems = (items: readonly StudioItem[], attr: string): boolean => {
  const counts = new Map<string, number>();
  let carried = 0;
  for (const item of items) {
    const value = valueOf(item, attr);
    if (isPlaceholderValue(value)) continue;
    counts.set(value, (counts.get(value) ?? 0) + 1);
    carried += 1;
  }
  if (counts.size < 2) return false;
  const blocks = [...counts.values()].filter((count) => count >= MIN_ROW_BLOCK_ITEMS);
  if (blocks.length < 2) return false;
  return blocks.reduce((sum, count) => sum + count, 0) >= ROW_BLOCK_COVERAGE * carried;
};

/** `attr` cuts the whole wall into real blocks. A product without a value counts against
 *  the coverage, so a level only a few products carry (a sub-brand on the pods) never
 *  branches the wall. */
const blocksWall = (items: readonly StudioItem[], attr: string): boolean =>
  items.filter((item) => !isPlaceholderValue(valueOf(item, attr))).length >=
    ROW_BLOCK_COVERAGE * items.length && blocksItems(items, attr);

const blocksSomeScope = (scopes: readonly BlockingScope[], attr: string): boolean =>
  scopes.some((scope) => blocksItems(scope.items, attr));

const distinctCount = (items: readonly StudioItem[], attr: string): number =>
  new Set(items.map((item) => valueOf(item, attr)).filter((value) => !isPlaceholderValue(value)))
    .size;

/** The branch's row levels, outermost first, at most `MAX_ROW_LEVELS`. Each pick is the
 *  best-banded level that still cuts a scope into real blocks; within `ROW_TIE_MARGIN` the
 *  one with fewer values wins, then the declared order. */
export const rowLevelsOf = (
  branchItems: readonly StudioItem[],
  candidates: readonly string[],
  runs: readonly StudioRunGeometry[],
  rankOf: (attr: string) => number,
): string[] => {
  let scopes: readonly BlockingScope[] = rootBlockingScopes(branchItems, runs);
  const remaining = [...candidates];
  const rows: string[] = [];
  while (rows.length < MAX_ROW_LEVELS && remaining.length > 0) {
    const banded = remaining
      .filter((attr) => blocksSomeScope(scopes, attr))
      .map((attr) => ({
        attr,
        score: measureBandednessOf(branchItems, attr, runs, scopes).score,
        values: distinctCount(branchItems, attr),
      }))
      .filter((entry) => entry.score >= ROW_BAND_SCORE);
    if (banded.length === 0) break;
    const best = Math.max(...banded.map((entry) => entry.score));
    const pick = banded
      .filter((entry) => entry.score >= best - ROW_TIE_MARGIN)
      .sort((a, b) => a.values - b.values || rankOf(a.attr) - rankOf(b.attr))[0];
    rows.push(pick.attr);
    remaining.splice(remaining.indexOf(pick.attr), 1);
    scopes = splitBlockingScopes(scopes, pick.attr);
  }
  return rows;
};

/** `attr` cuts the branch into real blocks that own disjoint rows. */
export const isRowLevel = (
  branchItems: readonly StudioItem[],
  attr: string,
  runs: readonly StudioRunGeometry[],
): boolean => {
  const scopes = rootBlockingScopes(branchItems, runs);
  return (
    blocksSomeScope(scopes, attr) &&
    measureBandednessOf(branchItems, attr, runs, scopes).score >= ROW_BAND_SCORE
  );
};

/** Nothing inside the branch stands as a column, and something cuts it into rows. */
export const branchIsHorizontal = (
  branchItems: readonly StudioItem[],
  candidates: readonly string[],
  runs: readonly StudioRunGeometry[],
): boolean => {
  const scopes = rootBlockingScopes(branchItems, runs);
  const splitting = candidates.filter((attr) => blocksSomeScope(scopes, attr));
  if (splitting.length === 0) return false;
  if (
    splitting.some(
      (attr) => measureBlockingOf(branchItems, attr, runs, scopes).score >= VERTICAL_SCORE,
    )
  )
    return false;
  return splitting.some(
    (attr) => measureBandednessOf(branchItems, attr, runs, scopes).score >= ROW_BAND_SCORE,
  );
};

/** The level the wall reads by: the outermost blocking level, down to the brand, that reads as
 *  horizontal or vertical blocking, else the outermost blocking level. A level inside the brand
 *  never branches the wall. */
export const wallBranchOf = (
  items: readonly StudioItem[],
  levels: readonly string[],
  runs: readonly StudioRunGeometry[],
  brandAttr?: string,
): string | undefined => {
  const brandAt = brandAttr === undefined ? -1 : levels.indexOf(brandAttr);
  const upToBrand = brandAt < 0 ? levels : levels.slice(0, brandAt + 1);
  const blocking = upToBrand.filter((attr) => blocksWall(items, attr));
  const reads = (attr: string) =>
    measureBandednessOf(items, attr, runs).score >= ROW_BAND_SCORE ||
    measureBlockingOf(items, attr, runs).score >= VERTICAL_SCORE;
  return (brandAt < 0 ? blocking.slice(0, 1) : blocking).find(reads) ?? blocking[0];
};

export interface HorizontalWallReading {
  horizontal: boolean;
  /** The level whose values are the branches (brand). */
  branchAttr: string;
  /** Its column score on the wall; from `VERTICAL_SCORE` it stands as columns. */
  branchScore: number;
  /** Its row (band) score on the wall; from `ROW_BAND_SCORE` its values own rows. */
  branchBand: number;
  /** SKU share of the branches that read horizontal. */
  share: number;
  /** Branch value -> horizontally blocked inside. */
  branches: ReadonlyMap<string, boolean>;
  /** More than one branch: a one-branch wall never reads horizontal. */
  eligible: boolean;
}

const readsRows = (branchBand: number): boolean => branchBand >= ROW_BAND_SCORE;
const readsColumns = (branchScore: number): boolean => branchScore >= VERTICAL_SCORE;

/** The branch level reads the wall when it bands or stands as columns, and the clearer of
 *  the two wins when it does both (a tie stays columns). When it does neither, the wall
 *  reads horizontal if enough of its branches stack their own levels in rows. */
export const wallReadsHorizontal = (
  branchScore: number,
  branchBand: number,
  share: number,
): boolean => {
  const rows = readsRows(branchBand);
  const columns = readsColumns(branchScore);
  if (rows && columns) return branchBand - ROW_BAND_SCORE > branchScore - VERTICAL_SCORE;
  if (rows || columns) return rows;
  return share >= HORIZONTAL_BRANCH_SHARE;
};

/** Read the wall once. Branches with nothing that splits them do not vote. */
export const readHorizontalWall = (
  items: readonly StudioItem[],
  branchAttr: string,
  candidates: readonly string[],
  runs: readonly StudioRunGeometry[],
): HorizontalWallReading => {
  const branchScore = measureBlockingOf(items, branchAttr, runs).score;
  const branchBand = measureBandednessOf(items, branchAttr, runs).score;
  const groups = new Map<string, StudioItem[]>();
  for (const item of items) {
    const value = valueOf(item, branchAttr);
    if (isPlaceholderValue(value)) continue;
    const group = groups.get(value);
    if (group) group.push(item);
    else groups.set(value, [item]);
  }
  const inner = candidates.filter((attr) => attr !== branchAttr);
  const branches = new Map<string, boolean>();
  let voting = 0;
  let horizontalWeight = 0;
  for (const [value, group] of groups) {
    const scopes = rootBlockingScopes(group, runs);
    if (!inner.some((attr) => blocksSomeScope(scopes, attr))) continue;
    const horizontal = branchIsHorizontal(group, inner, runs);
    branches.set(value, horizontal);
    voting += group.length;
    if (horizontal) horizontalWeight += group.length;
  }
  const share = voting > 0 ? horizontalWeight / voting : 0;
  const eligible = groups.size > 1;
  return {
    horizontal: eligible && wallReadsHorizontal(branchScore, branchBand, share),
    branchAttr,
    branchScore,
    branchBand,
    share,
    branches,
    eligible,
  };
};

/** How far either measure may move before the reading counts as a close call: the row tie
 *  margin, reused, with no tuning of its own. */
export const WALL_CLOSE_CALL_MARGIN = ROW_TIE_MARGIN;

/** The branch level reads both ways at once, or moving any one measure by the margin would
 *  flip the reading or make it read both ways. A wall that cannot read horizontal
 *  (`eligible` false) has no reading to flip. A reading stored before the band was measured
 *  has no band, and is judged on the other two measures as it was then. */
export const isCloseCall = (
  branchScore: number,
  share: number,
  eligible: boolean,
  branchBand = 0,
): boolean => {
  if (!eligible) return false;
  const reading = wallReadsHorizontal(branchScore, branchBand, share);
  const margin = WALL_CLOSE_CALL_MARGIN;
  return [
    [branchScore, branchBand, share],
    [branchScore - margin, branchBand, share],
    [branchScore + margin, branchBand, share],
    [branchScore, branchBand - margin, share],
    [branchScore, branchBand + margin, share],
    [branchScore, branchBand, share - margin],
    [branchScore, branchBand, share + margin],
  ].some(
    ([score, band, nudgedShare]) =>
      (readsRows(band) && readsColumns(score)) ||
      wallReadsHorizontal(score, band, nudgedShare) !== reading,
  );
};

/** The draft's wall reading, for the studio's line and the plan trace. */
export interface WallReading {
  /** What the pipeline follows. */
  blocking: WallBlockingKind;
  /** What inference read off the base pog. */
  read: WallBlockingKind;
  overridden: boolean;
  closeCall: boolean;
  branchAttr: string;
  branchScore: number;
  /** Absent on a reading stored before the band was measured. */
  branchBand?: number;
  share: number;
  margin: number;
  /** At and above this branch score the brand stands as columns. */
  verticalScore: number;
  /** At and above this branch band the brand owns rows. */
  rowScore: number;
  /** At and above this share the wall reads horizontal. */
  horizontalShare: number;
}

/** Data only: the studio translates it where it renders (`WallReadingLine`). */
export const wallReadingOf = (wall: WallBlocking): WallReading => ({
  blocking: wall.blocking,
  read: wall.read,
  overridden: wall.blocking !== wall.read,
  closeCall: isCloseCall(wall.branchScore, wall.share, wall.eligible, wall.branchBand),
  branchAttr: wall.branchAttr,
  branchScore: wall.branchScore,
  ...(wall.branchBand !== undefined ? { branchBand: wall.branchBand } : {}),
  share: wall.share,
  margin: WALL_CLOSE_CALL_MARGIN,
  verticalScore: VERTICAL_SCORE,
  rowScore: ROW_BAND_SCORE,
  horizontalShare: HORIZONTAL_BRANCH_SHARE,
});

/** The draft with the merchant's brand shelves choice. The default, full shelves where close,
 *  is no field, so a release that keeps it ships the rule set it always did. */
export const setBrandShelves = (
  draft: StudioDraft,
  choice: NonNullable<StudioDraft["brandShelves"]>,
): StudioDraft => {
  if (choice !== "whereClose") return { ...draft, brandShelves: choice };
  const next = { ...draft };
  delete next.brandShelves;
  return next;
};

/** The merchant's one-click override: the draft now follows `blocking`. Choosing what the
 *  wall reads as again clears the override. */
export const setWallBlocking = (draft: StudioDraft, blocking: WallBlockingKind): StudioDraft =>
  !draft.wallBlocking || draft.wallBlocking.blocking === blocking
    ? draft
    : { ...draft, wallBlocking: { ...draft.wallBlocking, blocking } };

/** The merchant flips the wall's reading. On an untouched draft (no tree edit since inference,
 *  `isUntouchedSinceInference`) inference runs again with the new reading, so the walk order
 *  and the row levels follow it. On an edited draft only the reading changes: every step after
 *  inference follows it, and the merchant's edits stay. `inferredFor` is inference with that
 *  reading forced, and must return the same draft for the same reading. The brand shelves
 *  choice is no tree edit: it is left out of the comparison and carried onto the re-inference. */
export const chooseWallBlocking = (
  history: StudioHistory,
  blocking: WallBlockingKind,
  inferredFor: (blocking: WallBlockingKind) => StudioDraft,
): StudioDraft => {
  const draft = history.present;
  if (!draft.wallBlocking || draft.wallBlocking.blocking === blocking) return draft;
  const isInferred = (candidate: StudioDraft): boolean => {
    if (!candidate.wallBlocking) return false;
    const inferred = inferredFor(candidate.wallBlocking.blocking);
    const tree = candidate.brandShelves ? setBrandShelves(candidate, "whereClose") : candidate;
    // Drafts are plain data, stringified for history signatures; a remount re-infers anew.
    return tree === inferred || JSON.stringify(tree) === JSON.stringify(inferred);
  };
  if (!isUntouchedSinceInference(history, isInferred)) return setWallBlocking(draft, blocking);
  const inferred = inferredFor(blocking);
  return draft.brandShelves ? setBrandShelves(inferred, draft.brandShelves) : inferred;
};
